import { supabase, getCurrentUserId, isSupabaseConfigured } from "@/lib/supabase";
import type { UserAgentSkill } from "@/types/workflowEngine";

function mapRow(row: Record<string, unknown>): UserAgentSkill {
  return {
    id: row.id as string,
    userId: row.user_id as string,
    name: row.name as string,
    description: (row.description as string) ?? null,
    instructions: row.instructions as string,
    integrationScopes: (row.integration_scopes as string[]) ?? [],
    outputSchema: row.output_schema,
    isTemplate: Boolean(row.is_template),
    updatedAt: (row.updated_at as string) ?? null,
  };
}

class UserAgentSkillsService {
  async list(): Promise<UserAgentSkill[]> {
    if (!isSupabaseConfigured) return [];
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await supabase
      .from("user_agent_skills")
      .select("*")
      .eq("user_id", userId)
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => mapRow(r as Record<string, unknown>));
  }

  async getById(id: string): Promise<UserAgentSkill | null> {
    if (!isSupabaseConfigured) return null;
    const userId = await getCurrentUserId();
    if (!userId) return null;
    const { data, error } = await supabase
      .from("user_agent_skills")
      .select("*")
      .eq("id", id)
      .eq("user_id", userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ? mapRow(data as Record<string, unknown>) : null;
  }

  async create(skill: Omit<UserAgentSkill, "id" | "userId" | "updatedAt">): Promise<UserAgentSkill> {
    if (!isSupabaseConfigured) throw new Error("Supabase is not configured");
    const userId = await getCurrentUserId();
    if (!userId) throw new Error("Not authenticated");
    const { data, error } = await supabase
      .from("user_agent_skills")
      .insert({
        user_id: userId,
        name: skill.name,
        description: skill.description,
        instructions: skill.instructions,
        integration_scopes: skill.integrationScopes,
        output_schema: skill.outputSchema ?? null,
        is_template: skill.isTemplate,
      })
      .select()
      .single();
    if (error) throw new Error(error.message);
    if (!data) throw new Error("Failed to create skill");
    return mapRow(data as Record<string, unknown>);
  }

  async update(
    id: string,
    patch: Partial<Omit<UserAgentSkill, "id" | "userId" | "updatedAt">>
  ): Promise<UserAgentSkill> {
    if (!isSupabaseConfigured) throw new Error("Supabase is not configured");
    const userId = await getCurrentUserId();
    if (!userId) throw new Error("Not authenticated");
    const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (patch.name !== undefined) payload.name = patch.name;
    if (patch.description !== undefined) payload.description = patch.description;
    if (patch.instructions !== undefined) payload.instructions = patch.instructions;
    if (patch.integrationScopes !== undefined) payload.integration_scopes = patch.integrationScopes;
    if (patch.outputSchema !== undefined) payload.output_schema = patch.outputSchema;
    if (patch.isTemplate !== undefined) payload.is_template = patch.isTemplate;
    const { data, error } = await supabase
      .from("user_agent_skills")
      .update(payload)
      .eq("id", id)
      .eq("user_id", userId)
      .select()
      .single();
    if (error) throw new Error(error.message);
    if (!data) throw new Error("Failed to update skill");
    return mapRow(data as Record<string, unknown>);
  }

  async delete(id: string): Promise<void> {
    if (!isSupabaseConfigured) throw new Error("Supabase is not configured");
    const userId = await getCurrentUserId();
    if (!userId) throw new Error("Not authenticated");
    const { error } = await supabase.from("user_agent_skills").delete().eq("id", id).eq("user_id", userId);
    if (error) throw new Error(error.message);
  }
}

export const userAgentSkillsService = new UserAgentSkillsService();
